import { inject, Injectable } from '@angular/core';
import type { NavigationError } from '@angular/router';
import { ExternalRedirectService } from 'app/core/services/external-redirect.service';

/**
 * sessionStorage flag marking that we already hard-reloaded this tab once to
 * recover from a stale lazy chunk. Timestamped rather than boolean: the write
 * is the cooldown start, so it self-expires (no subscription needed to clear
 * it) and a tab that hits a *second*, later deploy can still recover.
 */
export const CHUNK_LOAD_RELOAD_FLAG = 'pfl.chunk-load-reload';

/**
 * Once the flag is this old it no longer suppresses a reload: long enough to
 * break a reload loop yet still let a tab recover when a *later* deploy
 * invalidates chunks again.
 */
export const CHUNK_LOAD_RELOAD_COOLDOWN_MS = 5 * 60_000;

/**
 * Known signatures of "the HTML page is older than the deployed chunks".
 * Angular's esbuild builder emits native `import()` for lazy routes, so the
 * failing message is the browser's module-loader error, not Webpack's
 * `ChunkLoadError` (matched on `name` for safety):
 *   Chrome/Edge: `TypeError: Failed to fetch dynamically imported module: …`
 *   Firefox:     `TypeError: error loading dynamically imported module: …`
 *   Safari:      `TypeError: Importing a module script failed.`
 */
const CHUNK_LOAD_ERROR_RE =
    /dynamically imported module|Importing a module script failed/i;

/**
 * Whether `error` is a stale-lazy-chunk failure rather than a genuine bug in a
 * lazy route's module (a throw inside the module's own code must NOT trigger a
 * reload — the reload would not fix it and would hide the report).
 */
export function isStaleChunkError(error: unknown): boolean {
    if (!(error instanceof Error)) {
        return false;
    }
    return (
        error.name === 'ChunkLoadError' ||
        CHUNK_LOAD_ERROR_RE.test(error.message)
    );
}

/**
 * #2200 (PFL-FE-WEB-G / PFL-FE-WEB-2): after a deploy, a tab holding the
 * previous `index.html` still requests lazy chunks by their old content hash;
 * those files are gone, so every lazy navigation fails with a chunk-load
 * `NavigationError` (18 Sentry events / 14d) and the SPA stays wedged until
 * the user reloads manually. The only fix is a full reload so the browser
 * fetches the fresh `index.html` + chunk manifest.
 *
 * Wired via `withNavigationErrorHandler` in `app.config.ts`. Non-chunk errors,
 * and a chunk error arriving inside the cooldown, return `void` — the router
 * then rethrows through its normal path and the Sentry `ErrorHandler` still
 * reports them.
 */
@Injectable({ providedIn: 'root' })
export class ChunkLoadRecoveryService {
    private readonly _redirect = inject(ExternalRedirectService);

    handleNavigationError(navigationError: NavigationError): void {
        if (!isStaleChunkError(navigationError.error)) {
            return;
        }
        if (this._reloadedRecently()) {
            return;
        }
        try {
            sessionStorage.setItem(CHUNK_LOAD_RELOAD_FLAG, String(Date.now()));
        } catch {
            // Storage blocked (privacy mode / SecurityError): without the
            // flag a reload could loop forever, so skip the reload and let
            // the error be reported instead.
            return;
        }
        // Flag written BEFORE the navigation: `location.assign` is async, so
        // even if it somehow failed the loop guard still stands.
        this._redirect.assign(navigationError.url);
    }

    private _reloadedRecently(): boolean {
        try {
            const raw = sessionStorage.getItem(CHUNK_LOAD_RELOAD_FLAG);
            const reloadedAt = raw === null ? NaN : Number(raw);
            return (
                Number.isFinite(reloadedAt) &&
                Date.now() - reloadedAt < CHUNK_LOAD_RELOAD_COOLDOWN_MS
            );
        } catch {
            // Unreadable storage must be treated as "flag present" — the
            // matching setItem would throw anyway and suppress the reload.
            return true;
        }
    }
}
